import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, apiFetch, setApiUnauthorizedHandler } from '../utils/api';
export const VerificationEmailStatus = {
    accepted: 'accepted',
    sent: 'sent',
    logged: 'logged',
    failed: 'failed',
};
const AuthContext = createContext(null);
/**
 * Fixed session for tests and Storybook (no /api/auth/session). Syncs when `user` identity changes.
 */
export function StaticAuthProvider({ user: initialUser, children, }) {
    const [user, setUser] = useState(initialUser);
    useEffect(() => {
        setUser(initialUser);
    }, [initialUser]);
    const clearAuthState = useCallback(() => {
        setUser(null);
    }, []);
    const refreshSession = useCallback(async () => { }, []);
    const login = useCallback(async (_input) => {
        throw new Error('StaticAuthProvider: login is not wired');
    }, []);
    const register = useCallback(async (_input) => {
        throw new Error('StaticAuthProvider: register is not wired');
    }, []);
    const logout = useCallback(async () => {
        clearAuthState();
    }, [clearAuthState]);
    const value = useMemo(() => ({
        user,
        setUser,
        isLoading: false,
        isAuthenticated: user !== null,
        needsEmailVerification: false,
        refreshSession,
        login,
        register,
        logout,
    }), [login, logout, refreshSession, register, user]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
    const clearAuthState = useCallback(() => {
        setUser(null);
        setNeedsEmailVerification(false);
        setIsLoading(false);
    }, []);
    const applySession = useCallback((session) => {
        setUser(session.user);
        setNeedsEmailVerification(session.needsEmailVerification);
        setIsLoading(false);
    }, []);
    const refreshSession = useCallback(async () => {
        const session = await apiFetch('/api/auth/session', {
            skipAuthRedirect: true,
        });
        applySession(session);
    }, [applySession]);
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        apiFetch('/api/auth/session', {
            skipAuthRedirect: true,
        })
            .then((session) => {
            if (!cancelled) {
                applySession(session);
            }
        })
            .catch(() => {
            if (!cancelled) {
                clearAuthState();
            }
        });
        return () => {
            cancelled = true;
        };
    }, [applySession, clearAuthState]);
    useEffect(() => {
        setApiUnauthorizedHandler(() => {
            clearAuthState();
        });
        return () => {
            setApiUnauthorizedHandler(null);
        };
    }, [clearAuthState]);
    const login = useCallback(async ({ email, password }) => {
        const result = await apiFetch('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
            skipAuthRedirect: true,
        });
        setUser(result.user);
        setNeedsEmailVerification(result.needsEmailVerification);
        setIsLoading(false);
        return result;
    }, []);
    const register = useCallback(async ({ name, email, password }) => {
        await apiFetch('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify({ name, email, password }),
            skipAuthRedirect: true,
        });
        return login({ email, password });
    }, [login]);
    const logout = useCallback(async () => {
        try {
            await apiFetch('/api/auth/logout', {
                method: 'POST',
                skipAuthRedirect: true,
            });
        }
        catch (error) {
            if (!(error instanceof ApiError) || error.status !== 401) {
                throw error;
            }
        }
        finally {
            clearAuthState();
        }
    }, [clearAuthState]);
    const value = useMemo(() => ({
        user,
        setUser,
        isLoading,
        isAuthenticated: user !== null,
        needsEmailVerification,
        refreshSession,
        login,
        register,
        logout,
    }), [isLoading, login, logout, needsEmailVerification, refreshSession, user]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx)
        throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
/**
 * Returns the current user when inside AuthProvider; otherwise null.
 * Use for UI that should degrade gracefully in tests or isolated mounts.
 */
export function useOptionalUser() {
    const ctx = useContext(AuthContext);
    return ctx?.user ?? null;
}
